import { mutationOptions, queryOptions } from '@tanstack/react-query';

import type { Stored } from './stored';
import { apiClient, apiSiblingUrl } from './client';

import type { components } from './generated/rbacApi';
import { apiBaseUrl } from '../runtimeConfig';

type Schemas = components['schemas'];

/* A grant as the page reads it: the role grantee and the marking of
   generated grants are wishes implemented mock-first (see
   doc/backend-api-rbac-wishlist.md), so a grant names either a subject
   or a role as grantee. */
export type Grant = Omit<Schemas['RbacGrant'], 'granteeSubject.uuid'> & {
  'granteeSubject.uuid'?: string;
  'granteeRole.uuid'?: string;
  granteeRoleIdName?: string;
  manual?: boolean;
};

/* the role grantee is not in the API definition yet, see the note on
   postGrant below */
type GrantInsert = {
  grantedRoleUuid: string;
  granteeSubjectUuid?: string | null;
  granteeRoleUuid?: string | null;
  assumed: boolean;
};

/*
 * What the backend says about the logged-in subject
 * (`GET /api/rbac/context`): whether that subject is a global admin —
 * the office master data may be written by nobody else — and who the
 * subject is, which the grants page needs to tell "mine" from
 * "foreign" (REQ_008).
 *
 * The question is about the real user, never about a role they have
 * assumed in the sidebar, and that holds from both ends: the endpoint
 * lies outside the HSAdmin Office module, so the assumed-roles interceptor
 * cannot reach it (see `apiSiblingUrl` and `assumedRoles.ts`), and the
 * backend determines the flag before it applies any assumed role at
 * all (RbacContextController).
 */
export const rbacContextUrl = (base?: string) =>
  apiSiblingUrl('/rbac/context', base);

export const rbacGrantsUrl = (base?: string) =>
  apiSiblingUrl('/rbac/grants', base);

export const rbacSubjectsUrl = (base?: string) =>
  apiSiblingUrl('/rbac/subjects', base);

export const rbacRolesUrl = (base?: string) =>
  apiSiblingUrl('/rbac/roles', base);

export const rbacKeys = {
  context: ['rbac', 'context'] as const,
  grants: ['rbac', 'grants'] as const,
  subjects: (name: string | undefined) =>
    ['rbac', 'subjects', name ?? ''] as const,
  roles: ['rbac', 'roles'] as const,
};

export const rbacQueries = {
  context: () =>
    queryOptions({
      queryKey: rbacKeys.context,
      queryFn: async () => {
        const { data } =
          await apiClient.get<Schemas['RbacContext']>(rbacContextUrl());
        return data;
      },
      /* no configured API means no backend to ask — under jest, where
         the variable is unset, this keeps the query from firing a
         request at whatever host the test runner happens to be */
      enabled: Boolean(apiBaseUrl()),
      /* who the user is does not change under their hands: asking once
         per session is enough */
      staleTime: Infinity,
      /* an unanswered question counts as "not a global admin" (see
         `useGlobalAdmin`), and repeating it would not change that —
         the login behind it is the same one either way */
      retry: false,
    }),

  /* every grant visible to the logged-in user; narrowing happens
     client-side until the backend offers filters (see
     doc/backend-api-rbac-wishlist.md) */
  grants: () =>
    queryOptions({
      queryKey: rbacKeys.grants,
      queryFn: async () => {
        const { data } = await apiClient.get<Grant[]>(rbacGrantsUrl());
        return data;
      },
    }),
};

export const rbacSubjectQueries = {
  /* the grant form's who-search; asking without a name would list
     every subject of the system */
  byName: (name: string | undefined) =>
    queryOptions({
      queryKey: rbacKeys.subjects(name),
      queryFn: async () => {
        const { data } = await apiClient.get<Stored<Schemas['RbacSubject']>[]>(
          rbacSubjectsUrl(),
          {
            params: { name },
          }
        );
        return data;
      },
      enabled: Boolean(name),
    }),
};

export const rbacRoleQueries = {
  list: () =>
    queryOptions({
      queryKey: rbacKeys.roles,
      queryFn: async () => {
        const { data } =
          await apiClient.get<Stored<Schemas['RbacRole']>[]>(rbacRolesUrl());
        return data;
      },
    }),
};

export const rbacMutations = {
  /* the grantee is a subject or a role — the role grantee is a wished
     API extension implemented mock-first (see
     doc/backend-api-rbac-wishlist.md) */
  postGrant: () =>
    mutationOptions({
      mutationFn: async ({
        grantedRoleUuid,
        granteeSubjectUuid,
        granteeRoleUuid,
        assumed,
      }: GrantInsert) => {
        const { data } = await apiClient.post<Grant>(rbacGrantsUrl(), {
          'grantedRole.uuid': grantedRoleUuid,
          ...(granteeSubjectUuid
            ? { 'granteeSubject.uuid': granteeSubjectUuid }
            : {}),
          ...(granteeRoleUuid ? { 'granteeRole.uuid': granteeRoleUuid } : {}),
          assumed,
        });
        return data;
      },
    }),

  /* a grant is addressed by its two ends, it has no uuid of its own */
  deleteGrant: () =>
    mutationOptions({
      mutationFn: async ({
        grantedRoleUuid,
        granteeUuid,
      }: {
        grantedRoleUuid: string;
        /* a subject or a role, whichever end the grant names */
        granteeUuid: string | null | undefined;
      }) => {
        await apiClient.delete(
          `${rbacGrantsUrl()}/${grantedRoleUuid}/${granteeUuid}`
        );
      },
    }),
};
